import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Key,
  Refresh,
  Delete,
  ContentCopy,
  CheckCircle,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface PixKey {
  id: string;
  type: string;
  key: string;
  status: string;
  createdAt: string;
  owner?: {
    nome?: string;
    cpf?: string;
    cnpj?: string;
  };
}

const PixKeysPage: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState<PixKey[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newKeyType, setNewKeyType] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/pix/keys');

      if (response?.success) {
        setKeys(response.data || []);
      } else {
        setError(response?.error?.message || 'Erro ao carregar chaves PIX');
      }
    } catch (err: any) {
      console.error('Error loading PIX keys:', err);
      setError(err.message || 'Erro ao carregar chaves PIX');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('Chave copiada!');
  };

  const handleDeleteKey = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta chave PIX?')) {
      return;
    }

    setDeleting(id);
    try {
      const response = await api.delete(`/pix/keys/${id}`);

      if (response?.success) {
        toast.success('Chave excluída com sucesso');
        loadKeys();
      } else {
        toast.error(response?.error?.message || 'Erro ao excluir chave');
      }
    } catch (err: any) {
      console.error('Error deleting PIX key:', err);
      toast.error(err.message || 'Erro ao excluir chave PIX');
    } finally {
      setDeleting(null);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyType) {
      toast.error('Selecione o tipo da chave');
      return;
    }

    if (!newKeyValue) {
      toast.error('Informe o valor da chave criada no App Sicoob');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/pix/keys', {
        tipoChave: newKeyType,
        chave: newKeyValue.trim(),
      });

      if (response?.success) {
        toast.success('Chave cadastrada com sucesso');
        setOpenDialog(false);
        setNewKeyType('');
        setNewKeyValue('');
        loadKeys();
      } else {
        toast.error(response?.error?.message || 'Erro ao cadastrar chave');
      }
    } catch (err: any) {
      console.error('Error creating PIX key:', err);
      toast.error(err.message || 'Erro ao cadastrar chave PIX');
    } finally {
      setSaving(false);
    }
  };

  const getStatusChip = (status: string) => {
    const isActive = status === 'active' || status === 'ATIVA';
    return (
      <Chip
        icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <ErrorIcon sx={{ fontSize: 14 }} />}
        label={isActive ? 'Ativa' : status}
        size="small"
        sx={{
          bgcolor: alpha(isActive ? theme.palette.success.main : theme.palette.error.main, 0.12),
          color: isActive ? 'success.main' : 'error.main',
          '& .MuiChip-icon': { color: isActive ? 'success.main' : 'error.main' },
        }}
      />
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const maskKey = (type: string, key: string) => {
    if (!key) return '-';

    switch (type.toUpperCase()) {
      case 'CPF':
        return key.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '***.$2.***-**');
      case 'CNPJ':
        return key.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '**.$2.$3/****-**');
      case 'E-MAIL':
      case 'EMAIL':
        const [user, domain] = key.split('@');
        if (user && domain) {
          const maskedUser = user.substring(0, 3) + '***';
          return `${maskedUser}@${domain}`;
        }
        return key;
      case 'CELULAR':
      case 'TELEFONE':
        return key.replace(/(\+\d{2})(\d{2})(\d{5})(\d{4})/, '$1 $2 *****-$4');
      default:
        return key;
    }
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="rounded" width={150} height={40} />
        </Box>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <Key sx={{ mr: 1, verticalAlign: 'middle' }} />
          Chaves PIX
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadKeys}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpenDialog(true)}>
            Cadastrar Chave
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadKeys}>
              Tentar Novamente
            </Button>
          }
        >
          <AlertTitle>Erro ao carregar chaves</AlertTitle>
          {error}
        </Alert>
      )}

      {!error && keys.length === 0 && (
        <Alert
          severity="info"
          icon={<Info />}
          sx={{
            mb: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <AlertTitle>Nenhuma chave PIX cadastrada</AlertTitle>
          Cadastre sua primeira chave PIX para começar a receber pagamentos instantâneos.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Chave</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Data Cadastro</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 && !error ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Key sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    Nenhuma chave PIX cadastrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Chip
                      label={row.type}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontFamily="monospace">
                        {maskKey(row.type, row.key)}
                      </Typography>
                      <Tooltip title="Copiar chave completa">
                        <IconButton size="small" onClick={() => handleCopyKey(row.key)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(row.status)}
                  </TableCell>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Excluir chave">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteKey(row.id)}
                        disabled={deleting === row.id}
                      >
                        {deleting === row.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Delete fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para cadastrar nova chave */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cadastrar Chave PIX</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Importante</AlertTitle>
              As chaves PIX devem ser criadas no <strong>App Sicoob</strong> ou <strong>Internet Banking</strong>.
              <br />
              Acesse: <em>Pix → Minhas Chaves → Cadastrar Chave</em>
              <br /><br />
              Após criar a chave, copie e cole aqui para registrar no sistema.
            </Alert>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Chave</InputLabel>
              <Select
                value={newKeyType}
                label="Tipo de Chave"
                onChange={(e) => {
                  setNewKeyType(e.target.value);
                  setNewKeyValue('');
                }}
              >
                <MenuItem value="EVP">Chave Aleatória (EVP) - Recomendado</MenuItem>
                <MenuItem value="CPF">CPF</MenuItem>
                <MenuItem value="CNPJ">CNPJ</MenuItem>
                <MenuItem value="EMAIL">E-mail</MenuItem>
                <MenuItem value="CELULAR">Celular</MenuItem>
              </Select>
            </FormControl>

            {newKeyType && (
              <TextField
                fullWidth
                label="Cole a chave criada no Sicoob"
                value={newKeyValue}
                onChange={(e) => setNewKeyValue(e.target.value)}
                placeholder={
                  newKeyType === 'EVP' ? 'Ex: a1b2c3d4-e5f6-7890-abcd-ef1234567890' :
                  newKeyType === 'CPF' ? '00000000000' :
                  newKeyType === 'CNPJ' ? '00000000000000' :
                  newKeyType === 'EMAIL' ? 'exemplo@email.com' :
                  '+5511999999999'
                }
                helperText={
                  newKeyType === 'EVP'
                    ? 'A chave aleatória é um código UUID gerado pelo Sicoob. Ela não expõe dados pessoais.'
                    : 'Copie exatamente como aparece no App Sicoob'
                }
                multiline={newKeyType === 'EVP'}
                rows={newKeyType === 'EVP' ? 2 : 1}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateKey}
            disabled={saving || !newKeyType || !newKeyValue}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Add />}
          >
            {saving ? 'Cadastrando...' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PixKeysPage;
